import { type MutableRefObject, forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { Theme } from '../types/enums'
import { FAIcon, IconStyle } from '../Icons/Icons';
import { useBreakpoint } from '../Breakpoint/BreakpointProvider';
import styles from './Tooltip.module.scss';
import { TooltipPreference } from './types/enums';
import type {
  LRDirConfig,
  BaseDirConfigPropType,
  NextLRDirConfig,
  TBDirConfigProptype,
  TooltipPropsTypes,
} from './types/propsType';

const cx = classNames.bind(styles);

const {
  LEFT,
  RIGHT,
  TOP_CENTER,
  TOP_LEFT,
  TOP_RIGHT,
  BOTTOM_CENTER,
  BOTTOM_LEFT,
  BOTTOM_RIGHT,
  RIGHT_CENTER,
  RIGHT_BOTTOM,
  LEFT_BOTTOM,
  LEFT_CENTER,
} = TooltipPreference;
const filterDirection = (inputObject: LRDirConfig): BaseDirConfigPropType | undefined =>
  Object.entries(inputObject).find((entry) => {
    const { isDirection } = entry[1] as BaseDirConfigPropType;
    return isDirection;
  })?.[1];
function getLRDirection(
  currentDirConfig: LRDirConfig,
  nextDirConfig: NextLRDirConfig,
  canCompleteTop: boolean,
  canCompleteBottom: boolean,
  preference: string,
) {
  const currentDir = filterDirection(currentDirConfig),
    nextDir = filterDirection(nextDirConfig);
  if (currentDir) {
    return currentDir.direction;
  } else if (canCompleteTop) {
    return TOP_CENTER;
  } else if (canCompleteBottom) {
    return BOTTOM_CENTER;
  } else if (nextDir) {
    return nextDir.direction;
  }
  return preference;
}
function getTBDirection(
  fallbackPrimaryDir: BaseDirConfigPropType,
  fallbackSecondaryDir: BaseDirConfigPropType,
  currentDirConfig: TBDirConfigProptype,
  nextDirconfig: TBDirConfigProptype,
  prefernce: string,
): string {
  if (currentDirConfig.subsequentPrimaryDir?.isDirection) {
    return currentDirConfig.subsequentPrimaryDir.direction;
  } else if (currentDirConfig.subsequentSecondaryDir?.isDirection) {
    return currentDirConfig.subsequentSecondaryDir.direction;
  } else if (fallbackPrimaryDir.isDirection) {
    return fallbackPrimaryDir.direction;
  } else if (fallbackSecondaryDir.isDirection) {
    return fallbackSecondaryDir.direction;
  } else if (nextDirconfig.subsequentPrimaryDir?.isDirection) {
    return nextDirconfig.subsequentPrimaryDir.direction;
  } else if (nextDirconfig.subsequentSecondaryDir?.isDirection) {
    return nextDirconfig.subsequentSecondaryDir.direction;
  } else if (nextDirconfig.isDirection) {
    return nextDirconfig.defaultDir;
  }
  return prefernce;
}
function getTBPayload(positioningFlag: Record<string, boolean>, primaryDirection: string, secondaryDirection: string) {
  const {
    canTop,
    canCompleteTop,
    canCompleteBottom,
    canBottom,
    canCompleteRight,
    canTopBottomRight,
    canCompleteLeft,
    canTopBottomLeft,
  } = positioningFlag;
  const completeLeft: BaseDirConfigPropType = {
      direction: LEFT,
      isDirection: canCompleteLeft as boolean,
    },
    completeRight = {
      direction: RIGHT,
      isDirection: canCompleteRight as boolean,
    },
    topRight = {
      isDirection: (canTop as boolean) && (canTopBottomRight as boolean),
      direction: TOP_RIGHT,
    },
    topLeft = {
      isDirection: (canTop as boolean) && (canTopBottomLeft as boolean),
      direction: TOP_LEFT,
    },
    bottomRight = {
      isDirection: (canBottom as boolean) && (canTopBottomRight as boolean),
      direction: BOTTOM_RIGHT,
    },
    bottomLeft = {
      isDirection: (canBottom as boolean) && (canTopBottomLeft as boolean),
      direction: BOTTOM_LEFT,
    };
  // Default payload for TOP_LEFT;
  let fallbackPrimaryDir: BaseDirConfigPropType = completeLeft,
    fallbackSecondaryDir: BaseDirConfigPropType = completeRight;
  const currentDirConfig: TBDirConfigProptype = {
      defaultDir: TOP_CENTER,
      isDirection: (canTop as boolean) && ((canTopBottomRight as boolean) || (canTopBottomLeft as boolean)),
      subsequentPrimaryDir: topLeft,
      subsequentSecondaryDir: topRight,
    },
    nextDirconfig: TBDirConfigProptype = {
      defaultDir: BOTTOM_CENTER,
      isDirection: canCompleteBottom as boolean,
      subsequentPrimaryDir: {
        isDirection: canTopBottomLeft as boolean,
        direction: BOTTOM_LEFT,
      },
      subsequentSecondaryDir: { isDirection: canTopBottomRight as boolean, direction: BOTTOM_RIGHT },
    };
  if (secondaryDirection === RIGHT) {
    fallbackPrimaryDir = completeRight;
    fallbackSecondaryDir = completeLeft;
    currentDirConfig.subsequentPrimaryDir = topRight;
    currentDirConfig.subsequentSecondaryDir = topLeft;
  }
  if (primaryDirection === 'bottom') {
    currentDirConfig.defaultDir = BOTTOM_CENTER;
    currentDirConfig.isDirection =
      (canBottom as boolean) && ((canTopBottomRight as boolean) || (canTopBottomLeft as boolean));
    nextDirconfig.defaultDir = TOP_CENTER;
    nextDirconfig.isDirection = canCompleteTop as boolean;
    nextDirconfig.subsequentPrimaryDir = topLeft;
    nextDirconfig.subsequentSecondaryDir = topRight;
    if (secondaryDirection === LEFT) {
      currentDirConfig.subsequentPrimaryDir = bottomLeft;
      currentDirConfig.subsequentSecondaryDir = bottomRight;
    } else {
      currentDirConfig.subsequentPrimaryDir = bottomRight;
      currentDirConfig.subsequentSecondaryDir = bottomLeft;
    }
  }
  return {
    fallbackPrimaryDir,
    fallbackSecondaryDir,
    currentDirConfig,
    nextDirconfig,
  };
}
function getLRPayload(positioningFlag: Record<string, boolean>, direction: string) {
  const { canTop, canBottom, canCompleteRight, canTopBottomRight, canCompleteLeft, canTopBottomLeft } = positioningFlag;
  const completeLeft = {
      direction: LEFT,
      isDirection: canCompleteLeft,
    },
    completeRight = {
      direction: RIGHT,
      isDirection: canCompleteRight,
    },
    topRight: BaseDirConfigPropType = {
      isDirection: (canTop as boolean) && (canTopBottomRight as boolean),
      direction: TOP_RIGHT,
    },
    topLeft = {
      isDirection: canTopBottomLeft,
      direction: TOP_LEFT,
    },
    bottomRight = {
      isDirection: canBottom && canTopBottomRight,
      direction: BOTTOM_RIGHT,
    },
    bottomLeft = {
      isDirection: canTopBottomLeft,
      direction: BOTTOM_LEFT,
    };
  let currentDirConfig: LRDirConfig = {
      primaryDir: topRight,
      secondaryDir: bottomRight as BaseDirConfigPropType,
    },
    nextDirConfig: NextLRDirConfig = {
      primaryDir: topLeft as BaseDirConfigPropType,
      secondaryDir: completeLeft as BaseDirConfigPropType,
      tertiaryDir: bottomLeft as BaseDirConfigPropType,
    };
  if (direction === LEFT) {
    currentDirConfig = {
      primaryDir: topLeft as BaseDirConfigPropType,
      secondaryDir: bottomLeft as BaseDirConfigPropType,
    };
    nextDirConfig = {
      primaryDir: topRight,
      secondaryDir: completeRight as BaseDirConfigPropType,
      tertiaryDir: bottomRight as BaseDirConfigPropType,
    };
  }
  return { currentDirConfig, nextDirConfig };
}

/**
 *
 * @description Tooltip Provides a brief, informative message that
 *  appears when a user interacts with an information icon.
 */

const Tooltip = forwardRef<HTMLSpanElement, TooltipPropsTypes>(
  (
    {
      preference = TooltipPreference.TOP_CENTER,
      message,
      children,
      className = '',
      iconName = 'circle-info',
      theme = Theme.PURPLE,
      iconStyle = IconStyle.REGULAR,
    }: TooltipPropsTypes,
    ref,
  ) => {
    const tooltipRef = useRef() as MutableRefObject<HTMLDivElement>;
    const tooltipIconRef = useRef() as MutableRefObject<HTMLButtonElement>;
    const viewport = useBreakpoint();
    const [currentDirection, setCurrentDirection] = useState('');
    let viewportWidth = 0;
    let viewportHeight = 0;

    if (typeof document !== 'undefined') {
      const { clientWidth, clientHeight } = document.documentElement;
      viewportWidth = clientWidth;
      viewportHeight = clientHeight;
    }

    useImperativeHandle(ref, () => tooltipRef.current);

    const handleMouseOver = useCallback(() => {
      if (tooltipRef.current && tooltipIconRef.current) {
        getTooltipLocation(tooltipRef.current.getBoundingClientRect(), tooltipIconRef.current.getBoundingClientRect());
      }
    }, [tooltipRef, tooltipIconRef, viewport, preference, message]);

    /**
     *
     * @param newDirection string from location calcultaion
     * @param iconRef tooltip display icon ref.
     * @description function runs in case when no appropritate location found to display tooltip.
     */
    function processPreferneceUI(newDirection: string, iconRef: DOMRect) {
      if (tooltipRef.current && preference === newDirection) {
        const [primaryDirection] = newDirection.split('-');
        const TOOLTIP_SPACING = 15; // Space between icon and tooltip

        let availableSpace = 0;

        // Calculate available space based on the direction
        switch (primaryDirection) {
          case 'right':
            availableSpace = viewportWidth - iconRef.right - TOOLTIP_SPACING;
            break;
          case 'left':
            availableSpace = iconRef.left - TOOLTIP_SPACING;
            break;
          case 'top':
          case 'bottom':
            // For top/bottom, calculate available horizontal space from icon position
            availableSpace = Math.min(iconRef.left, viewportWidth - iconRef.right) * 2; // Approximate available width
            break;
          default:
            availableSpace = viewportWidth;
        }

        // Use the smaller of: calculated available space or viewport-based fallback
        const optimalWidth = Math.min(availableSpace, viewportWidth);

        // Only set if it's smaller than the current natural width
        tooltipRef.current.style.maxWidth = `${Math.max(optimalWidth, 200)}px`; // Minimum 200px for readability
      }
      setCurrentDirection(newDirection);
    }

    /**
     *
     * @param messageRef tooltip message cloud reference
     * @param iconRef tooltip display icon ref.
     * @description function provide appropriate location for tooltip
     */
    function getTooltipLocation(messageRef: DOMRect, iconRef: DOMRect) {
      const {
        height: tooltipMessageHeight,
        width: tooltipMessageWidth,
        left: toolTipLeft,
        right: toolTipRight,
      } = messageRef;
      const {
        bottom: tooltipIconBottom,
        left: tooltipIconLeft,
        right: tooltipIconRight,
        top: tooltipIconTop,
      } = iconRef;

      const [primaryDirection, secondaryDirection] = preference.split('-');
      const isClipping = toolTipRight <= viewportWidth && toolTipLeft >= 0;
      // 15 is distance between tooltip icon and tooltip message box.
      const positioningFlag = {
        canCompleteTop: isClipping && tooltipMessageHeight + 15 <= tooltipIconTop,
        canCompleteBottom: isClipping && tooltipMessageHeight + 15 <= viewportHeight - tooltipIconBottom,
        canCompleteRight: viewportWidth > tooltipMessageWidth + tooltipIconRight + 15,
        canCompleteLeft: tooltipIconLeft > tooltipMessageWidth + 15,
        canTop: tooltipMessageHeight + 15 <= tooltipIconTop,
        canBottom: tooltipMessageHeight + 15 <= viewportHeight - tooltipIconBottom,
        canTopBottomRight: viewportWidth >= tooltipMessageWidth + tooltipIconRight,
        canTopBottomLeft: tooltipIconRight >= tooltipMessageWidth,
      };
      const { canCompleteTop, canCompleteBottom, canCompleteRight, canCompleteLeft } = positioningFlag;
      switch (primaryDirection) {
        case RIGHT:
          {
            if (canCompleteRight) {
              handleRightPosition(secondaryDirection as string);
              break;
            }
            const { currentDirConfig, nextDirConfig } = getLRPayload(positioningFlag, primaryDirection);
            const newDirection = getLRDirection(
              currentDirConfig,
              nextDirConfig,
              canCompleteTop,
              canCompleteBottom,
              preference,
            );
            processPreferneceUI(newDirection, iconRef);
          }
          break;
        case LEFT:
          {
            if (canCompleteLeft) {
              handleLeftPosition(secondaryDirection as string);
              break;
            }
            const { currentDirConfig, nextDirConfig } = getLRPayload(positioningFlag, primaryDirection);
            const newDirection = getLRDirection(
              currentDirConfig,
              nextDirConfig,
              canCompleteTop,
              canCompleteBottom,
              preference,
            );
            processPreferneceUI(newDirection, iconRef);
          }
          break;
        case 'top':
          {
            if (canCompleteTop && secondaryDirection === 'center') {
              setCurrentDirection(TOP_CENTER);
              break;
            }
            const { fallbackPrimaryDir, fallbackSecondaryDir, currentDirConfig, nextDirconfig } = getTBPayload(
              positioningFlag,
              'top',
              secondaryDirection as string,
            );
            const newDirection = getTBDirection(
              fallbackPrimaryDir,
              fallbackSecondaryDir,
              currentDirConfig,
              nextDirconfig,
              preference,
            );
            processPreferneceUI(newDirection, iconRef);
          }
          break;

        default:
          {
            if (canCompleteBottom && secondaryDirection === 'center') {
              setCurrentDirection(BOTTOM_CENTER);
              break;
            }
            const { fallbackPrimaryDir, fallbackSecondaryDir, currentDirConfig, nextDirconfig } = getTBPayload(
              positioningFlag,
              'bottom',
              secondaryDirection as string,
            );
            const newDirection = getTBDirection(
              fallbackPrimaryDir,
              fallbackSecondaryDir,
              currentDirConfig,
              nextDirconfig,
              preference,
            );
            processPreferneceUI(newDirection, iconRef);
          }
          break;
      }
    }

    const handleRightPosition = (secondaryDirection: string) => {
      if (secondaryDirection === 'center') {
        setCurrentDirection(RIGHT_CENTER);
      } else if (secondaryDirection === 'bottom') {
        setCurrentDirection(RIGHT_BOTTOM);
      } else {
        setCurrentDirection(RIGHT);
      }
    };
    const handleLeftPosition = (secondaryDirection: string) => {
      if (secondaryDirection === 'center') {
        setCurrentDirection(LEFT_CENTER);
      } else if (secondaryDirection === 'bottom') {
        setCurrentDirection(LEFT_BOTTOM);
      } else {
        setCurrentDirection(LEFT);
      }
    };

    const CClassname = cx('tooltip', [currentDirection, theme], className);
    return (
      <button
        id="nextgen-tooltip-component"
        className={CClassname}
        data-testid="tooltip-test-id"
        onMouseEnter={handleMouseOver}
        onMouseLeave={() => setCurrentDirection('')}
        ref={tooltipIconRef}
        onClick={(e) => e.preventDefault()}
      >
        <FAIcon iconName={iconName} theme={theme} className={styles['tooltip-info']} iconStyle={iconStyle} />
        <span className={styles['tooltip-text']} ref={tooltipRef} data-testid="tooltip-text-body-testid">
          <span data-testid="tooltip-message-test">{message ?? children}</span>
        </span>
      </button>
    );
  },
);
Tooltip.displayName = 'Tooltip';

export { TooltipPreference };
export default Tooltip;
